import { Check, Loader2, PlusIcon, Trash2, X } from "lucide-react";

import CustomChevronIcon from "../../ui/CustomChervonIcon";
import { CustomToolTip } from "../../ui/CustomToolTip";

import PaginatedLoader from "./PaginatedLoader";

import type { ButtonVariant } from "@/app/app.interface";
import Icons from "@/components/Icons";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import type { Side } from "@/components/ui/CustomChervonIcon";
import {
  Popover,
  PopoverContent,
  PopoverTriggerButton,
} from "@/components/ui/popover";
import { Any } from "@/interfaces/globals";
import { cn } from "@/utils/core/generalUtils";

interface AddButtonConfig {
  label?: string;
  variant?: ButtonVariant;
  className?: string;
  handleClick?: (scrollToBottom?: () => void) => void;
  icon?: string;
}

interface AsyncSingleSelectPopoverLoadingState {
  showSearchLoader: boolean;
  showMainSkeleton: boolean;
  showLoadMoreIndicator: boolean;
}

interface AsyncSingleSelectPopoverTriggerProps {
  className?: string;
  clearableConfig: {
    isClearable?: boolean;
    onClear?: () => void;
  };
  customIcon?: React.ReactNode;
  disabled: boolean;
  hasValidTriggerValue: boolean;
  isReadOnly: boolean;
  onKeyDown: (event: React.KeyboardEvent) => void;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  showArrow: boolean;
  side?: Side;
  triggerClassName?: string;
  triggerDisplayValue: React.ReactNode;
  triggerRef: React.RefObject<HTMLButtonElement | HTMLDivElement>;
  portal?: boolean;
}

interface AsyncSingleSelectPopoverSearchProps {
  label: string;
  loadingSkeleton?: React.ReactNode;
  loadingState: AsyncSingleSelectPopoverLoadingState;
  searchInputRef: React.RefObject<HTMLInputElement>;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

interface AsyncSingleSelectPopoverListProps<T> {
  allOptions: T[];
  deleteAction?: (id: string) => Promise<void>;
  deletingOptions: Set<string>;
  displayOptions: T[];
  hasError: boolean;
  filterQueryList?: (queryList: T[]) => T[];
  getOptionValue: (option: T) => string;
  getIsDisabled: (option: T) => boolean;
  handleDeleteOption: (
    optionValue: string,
    deleteActionFn?: (id: string) => Promise<void>,
  ) => Promise<void>;
  handleScroll: (event: React.UIEvent<HTMLDivElement>) => void;
  handleSelect: (value: string) => void;
  isFetching: boolean;
  isLoading: boolean;
  noResultsMessage?: string;
  notFound?: React.ReactNode;
  renderCustomValue?: (option: T) => React.ReactNode;
  renderOption: (option: T) => React.ReactNode;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  showCheckIcon: boolean;
  value?: string | null;
}

interface AsyncSingleSelectPopoverFooterProps {
  addButton?: false | AddButtonConfig;
  onClearSelection: () => void;
  scrollToBottom: () => void;
}

interface AsyncSingleSelectPopoverProps<T> {
  footerProps: AsyncSingleSelectPopoverFooterProps;
  listProps: AsyncSingleSelectPopoverListProps<T>;
  searchProps: AsyncSingleSelectPopoverSearchProps;
  triggerProps: AsyncSingleSelectPopoverTriggerProps;
}

export const AsyncSingleSelectPopover = <T,>(
  props: AsyncSingleSelectPopoverProps<T>,
): JSX.Element => {
  const { footerProps, listProps, searchProps, triggerProps } = props;
  const {
    className,
    clearableConfig,
    customIcon,
    disabled,
    hasValidTriggerValue,
    isReadOnly,
    onKeyDown,
    onOpenChange,
    open,
    showArrow,
    side,
    triggerClassName,
    triggerDisplayValue,
    triggerRef,
    portal = false,
  } = triggerProps;
  const {
    label,
    loadingSkeleton,
    loadingState,
    searchInputRef,
    searchTerm,
    setSearchTerm,
  } = searchProps;
  const {
    allOptions,
    deleteAction,
    deletingOptions,
    displayOptions,
    hasError,
    filterQueryList,
    getOptionValue,
    handleDeleteOption,
    handleScroll,
    handleSelect,
    isFetching,
    isLoading,
    noResultsMessage,
    notFound,
    renderCustomValue,
    renderOption,
    scrollContainerRef,
    showCheckIcon,
    value,
    getIsDisabled,
  } = listProps;
  const { addButton, onClearSelection, scrollToBottom } = footerProps;

  const TriggerComponent = isReadOnly ? "div" : PopoverTriggerButton;

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal={true}>
      <TriggerComponent
        ref={triggerRef as Any}
        role='combobox'
        className={cn(
          "justify-between",
          disabled && "opacity-50 cursor-not-allowed",
          triggerClassName,
          isReadOnly &&
            "text-left flex h-10 truncate items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
        )}
        contentClassName='w-11/12'
        disabled={disabled}
        side={side}
        onKeyDown={onKeyDown}
        isArrowVisible={showArrow && !hasValidTriggerValue}
        customIcon={
          clearableConfig.isClearable && hasValidTriggerValue ? (
            <X
              className='ml-2 h-4 w-4 opacity-50 hover:text-red-500'
              onClick={(event) => {
                event.stopPropagation();
                onClearSelection();
              }}
            />
          ) : (
            customIcon
          )
        }
      >
        {isReadOnly ? (
          <CustomToolTip
            toolTipContent={triggerDisplayValue}
            toolTipTrigger={
              <span className='line-clamp-1 w-10/12 truncate'>
                {triggerDisplayValue}
              </span>
            }
          />
        ) : (
          <span className={cn(!hasValidTriggerValue && "opacity-30")}>
            {triggerDisplayValue}
          </span>
        )}
        {isReadOnly && showArrow && (
          <CustomChevronIcon className='ml-2 h-4 w-4 opacity-50' side={side} />
        )}
      </TriggerComponent>
      <PopoverContent
        className={cn(
          "p-2 popover-content-width-full border border-input rounded-[0.5rem] shadow-select popover-content-width-full",
          "max-h-[min(300px,var(--radix-popover-content-available-height))] overflow-y-clip mb-4",
          className,
        )}
        align='start'
        portal={portal}
        side={side}
        avoidCollisions={true}
        collisionPadding={{ top: 10, bottom: 20 }}
        sideOffset={4}
      >
        <Command shouldFilter={false}>
          {loadingState.showMainSkeleton || loadingSkeleton ? (
            <PaginatedLoader />
          ) : (
            <>
              <div className='relative w-full'>
                <CommandInput
                  ref={searchInputRef}
                  placeholder={`Search ${label.toLowerCase()}...`}
                  value={searchTerm}
                  onValueChange={setSearchTerm}
                />
                {loadingState.showSearchLoader && (
                  <div className='absolute right-2 top-1/2 flex items-center transform -translate-y-1/2'>
                    <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                  </div>
                )}
              </div>
              <CommandList
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className='max-h-60 overflow-y-scroll scrollbar-none'
              >
                {hasError ? (
                  <div className='p-4 text-destructive text-center'>
                    Failed to fetch options
                  </div>
                ) : null}

                {!isLoading &&
                  !hasError &&
                  displayOptions.length === 0 &&
                  !loadingState.showMainSkeleton &&
                  (isFetching && allOptions.length > 0 && filterQueryList ? (
                    <CommandEmpty>Loading more options…</CommandEmpty>
                  ) : (
                    notFound || (
                      <CommandEmpty>
                        {noResultsMessage ?? `No ${label.toLowerCase()} found.`}
                      </CommandEmpty>
                    )
                  ))}

                {displayOptions.length > 0 && (
                  <CommandGroup>
                    {displayOptions.map((option) => {
                      const optionValue = getOptionValue(option);
                      const isSelected = value === optionValue;
                      const isDisabled = getIsDisabled(option);

                      return (
                        <CommandItem
                          key={optionValue}
                          value={optionValue}
                          onSelect={handleSelect}
                          disabled={isDisabled}
                          className={cn(
                            "relative p-2 content-stretch group text-sm justify-between data-[selected=true]:bg-transparent",
                            isDisabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer hover:bg-accent",
                            isSelected && "bg-[#2844bd19] hover:bg-[#2844bd19]",
                            deletingOptions.has(optionValue) &&
                              "opacity-50 pointer-events-none",
                          )}
                        >
                          {renderOption(option)}
                          {deleteAction && (
                            <div
                              className='invisible p-1 cursor-pointer group-hover:visible'
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                void handleDeleteOption(
                                  optionValue,
                                  deleteAction,
                                );
                              }}
                            >
                              {deletingOptions.has(optionValue) ? (
                                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                              ) : (
                                <Trash2 color='red' size={18} />
                              )}
                            </div>
                          )}
                          {isSelected && showCheckIcon && (
                            <Check className='ml-2 w-4 h-4 self-end' />
                          )}
                          {renderCustomValue?.(option)}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                )}

                {loadingState.showLoadMoreIndicator && (
                  <div className='p-3 text-center border-t bg-muted/5'>
                    <div className='flex items-center justify-center gap-2'>
                      <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
                      <p className='text-sm text-muted-foreground'>
                        Loading more...
                      </p>
                    </div>
                  </div>
                )}
              </CommandList>
              {addButton && (
                <Button
                  type='button'
                  variant={
                    `${addButton.variant ?? "secondary"}` as ButtonVariant
                  }
                  className={cn(
                    "flex justify-center items-center align-center w-full mt-2",
                    addButton.className,
                  )}
                  onClick={() => addButton.handleClick?.(scrollToBottom)}
                >
                  {addButton.icon ? (
                    <Icons name={addButton.icon} size={18} color='#fff' />
                  ) : (
                    <PlusIcon size={18} strokeWidth={1.8} className='mx-1' />
                  )}
                  <p className={cn(addButton.icon && "px-2")}>
                    {addButton.label}
                  </p>
                </Button>
              )}
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
};
